import { LogoutInfo, SignInRoute, TokenInfo } from '@/models';
import { setAuthState, logoutFx, refreshTokenFx } from '@/store';
import { isRoutePrivate, throttle } from '@/utils';

import { ITokenService, TokenService } from './token.service';
import { ITimerService, TimerService } from './timer.service';

const BASE_URL = window.location.origin;

export interface IAuthenticationService {
  onLogOut: () => void;
  onLogin: () => void;
  onRefresh: (tokenInfo: TokenInfo) => void;
  urls: Required<AuthUrls>;
  tokenInfo: ITokenService;
}

type AuthUrls = {
  api: string;
  refresh?: string;
  signIn?: string;
  signOut?: string;
  oktaUrl: string;
  oktaLogout: string;
};

type AuthenticationConfig = {
  loginRedirectUri: string;
  logoutRedirectUri: string;
  privateRoutes?: Record<string, string> | null;
  inactivityThresholdMinutes?: number;
  hasAccessDeniedModal?: boolean;
  hasLoginPage?: boolean;
  oktaLoginRedirectUri?: string;
  urls: AuthUrls;
};

export class Authentication implements IAuthenticationService {
  private _loginRedirectUri;
  private _logoutRedirectUri;
  private privateRoutes;
  private hasAccessDeniedModal;
  private _hasLoginPage;
  private _fullOktaAuthUrl;

  private timer: ITimerService;
  public tokenInfo: ITokenService;

  private _urls: Required<AuthUrls>;

  constructor({
    loginRedirectUri,
    logoutRedirectUri,
    privateRoutes = null,
    inactivityThresholdMinutes = 30,
    hasAccessDeniedModal = false,
    hasLoginPage = true,
    oktaLoginRedirectUri = SignInRoute.OktaSignInCallback,
    urls,
  }: AuthenticationConfig) {
    this._logoutRedirectUri = logoutRedirectUri;
    this._loginRedirectUri = loginRedirectUri;
    this.privateRoutes = privateRoutes;
    this.hasAccessDeniedModal = hasAccessDeniedModal;
    this._hasLoginPage = hasLoginPage;
    this._fullOktaAuthUrl = `${urls.oktaUrl}&redirect_uri=${BASE_URL}/${oktaLoginRedirectUri}`;

    this.timer = new TimerService(inactivityThresholdMinutes, privateRoutes, () => this.onLogOut());
    this.tokenInfo = new TokenService();

    this._urls = this.getDefaultUrls(urls);

    window.addEventListener('storage', this.throttledOnStorageChange);
  }

  get logoutRedirectUri() {
    return this._logoutRedirectUri;
  }

  get loginRedirectUri() {
    return this._loginRedirectUri;
  }

  get hasLoginPage() {
    return this._hasLoginPage;
  }

  get fullOktaAuthUrl() {
    return this._fullOktaAuthUrl;
  }

  get urls() {
    return this._urls;
  }

  private onStorageChange = (event: Event) => {
    this.timer.setNewTime(event);

    const tokenState = TokenService.get();

    if (!tokenState && isRoutePrivate(this.privateRoutes)) {
      void this.onLogOut();
    }
  };

  private throttledOnStorageChange = throttle(this.onStorageChange, 2000);

  onLogin = async () => {
    const tokenInfo = TokenService.get();

    if (!tokenInfo) {
      if (this.tokenInfo.authType === 'okta' && !this.hasLoginPage) {
        window.location.assign(this.fullOktaAuthUrl);

        return;
      }

      const url = `${BASE_URL}/${
        this.hasAccessDeniedModal ? SignInRoute.AccessDenied : this.logoutRedirectUri
      }`;
      window.location.assign(url);

      return;
    }

    if (tokenInfo.isExpired === false) {
      setAuthState({ authorized: true, authChecked: true });
    }
  };

  onRefresh = async (tokenInfo: TokenInfo) => {
    let authorized = false;
    let token = tokenInfo.token;

    try {
      const refreshedTokenInfo = await refreshTokenFx({ token, authType: tokenInfo.authType });

      if (refreshedTokenInfo) {
        token = refreshedTokenInfo.token;
        authorized = true;
      }
    } catch (e) {
      setAuthState({ authorized: false, authChecked: true });
      TokenService.set({ ...tokenInfo, isRefreshing: false });
    }

    return { authorized, token };
  };

  onLogOut = async () => {
    const tokenInfo = TokenService.get();

    if (tokenInfo) {
      try {
        const authType = this.tokenInfo.authType ?? tokenInfo.authType;

        const logoutInfo: LogoutInfo | null = await logoutFx({ authType });

        if (authType === 'okta') {
          const oktaIdToken = logoutInfo?.idPIdToken;

          if (oktaIdToken) {
            this.hasAccessDeniedModal &&
              history.pushState(null, '', `${BASE_URL}/${SignInRoute.AccessDenied}`);
            const logoutUrl = `${this._urls.oktaLogout}?id_token_hint=${oktaIdToken}&post_logout_redirect_uri=${BASE_URL}${this.logoutRedirectUri}`;

            window.location.assign(logoutUrl);
            this.tokenInfo.remove();

            return;
          }
        }
      } catch (e) {}
    }

    const url =
      this.tokenInfo.authType === 'okta' && !this.hasLoginPage
        ? this.fullOktaAuthUrl
        : `${BASE_URL}${this.logoutRedirectUri}`;

    window.location.assign(url);
    this.tokenInfo.remove();
  };

  private getDefaultUrls = (urls: AuthUrls): Required<AuthUrls> => ({
    ...urls,
    api: urls.api,
    refresh: urls?.refresh || `${urls.api}/auth/refreshToken`,
    signIn: urls?.signIn || `${urls.api}/auth/signin`,
    signOut: urls?.signOut || `${urls.api}/auth/signout`,
  });
}
